import React, { useRef, useEffect, useState } from "react";
import { formLabelClasses } from "@mui/material";
import { Corner, labelsColors, Rect, RectType } from "../../models/Rect";
type Props = {
  imageSrc: string;
  rectType: RectType|null;
  rects: Map<RectType, Rect>;
  setRects: (rects: Map<RectType, Rect>) => void;
  disabled? : boolean;
};

export const ImageCanvas: React.FC<Props> = ({
  imageSrc,
  rects,
  rectType,
  setRects,
  disabled=formLabelClasses
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [lastPointer, setLastPointer] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [lineWidth, setLineWidth] = useState<number>(1);
  const [resizingHandle, setResizingHandle] = useState<{
    rectType: RectType;
    corner: Corner;
  } | null>(null);
  const [tempRect, setTempRect] = useState<Rect | null>(null);
  const [rectMoving, setRectMoving] = useState<RectType | null>(null);
  const [dragOffset, setDragOffset] = useState<{
    offsetX: number;
    offsetY: number;
  } | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      canvas.width = img.width;
      // Stroke width is derived here and used directly rather than read from `lineWidth`
      // state: setLineWidth does not change the value already captured by this closure, and
      // lineWidth is not an effect dependency, so the first paint after an image loads
      // stroked at the initial 1px. On a 1920-wide frame scaled to ~750px that is a third of
      // a CSS pixel, so a SAVED box was drawn but invisible until some later interaction
      // happened to force a redraw with the settled value.
      const stroke = img.width / 400;
      setLineWidth(stroke);
      canvas.height = img.height;
      canvas.addEventListener("touchmove", preventTouchScroll, {
        passive: false,
      });
      ctx.drawImage(img, 0, 0);
      Array.from(rects.keys()).forEach((key) => {
        if (rects.get(key)!.visible) {
          ctx.strokeStyle = labelsColors[key];
          ctx.lineWidth = rects.get(key)!.selected ? 2 * stroke : stroke;
          ctx.strokeRect(
            rects.get(key)!.x,
            rects.get(key)!.y,
            rects.get(key)!.w,
            rects.get(key)!.h
          );

          if (rects.get(key)!.selected) {
            drawHandles(ctx, rects.get(key)!, stroke);
          }
        }
      });

      if (tempRect) {
        ctx.strokeStyle = "gray";
        ctx.setLineDash([6]);
        ctx.lineWidth = stroke;
        ctx.strokeRect(tempRect.x, tempRect.y, tempRect.w, tempRect.h);
        ctx.setLineDash([]);
      }
    };
    return () => {
      canvas.removeEventListener("touchmove", preventTouchScroll);
    };
  }, [imageSrc, rects, tempRect,rectType]);

  const drawHandles = (ctx: CanvasRenderingContext2D, r: Rect, stroke: number) => {
    const size = 6 * stroke;
    const handles = getHandlePositions(r);
    ctx.fillStyle = "white";
    ctx.strokeStyle = "black";
    handles.forEach(({ x, y }) => {
      ctx.fillRect(x - size / 2, y - size / 2, size, size);
      ctx.strokeRect(x - size / 2, y - size / 2, size, size);
    });
  };

  const getHandlePositions = (r: Rect) => [
    { corner: Corner.TopLeft, x: r.x, y: r.y },
    { corner: Corner.TopRight, x: r.x + r.w, y: r.y },
    { corner: Corner.BottomLeft, x: r.x, y: r.y + r.h },
    { corner: Corner.BottomRight, x: r.x + r.w, y: r.y + r.h },
  ];
  const preventTouchScroll = (e: TouchEvent) => {
    e.preventDefault();
  };

  const getPointerPosition = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ("touches" in e && e.touches.length > 0) {
      let pos = {
        x: Math.min(
          canvas.width,
          Math.max(0, e.touches[0].clientX - rect.left)*scaleX
        ),
        y: Math.min(
          canvas.height,
          Math.max(0, e.touches[0].clientY - rect.top)*scaleY
        ),
      };
      setLastPointer(pos);

      return pos;
    } else if ("clientX" in e) {
      return {
        x: Math.min(canvas.width, Math.max(0, e.clientX - rect.left))*scaleX,
        y: Math.min(canvas.height, Math.max(0, e.clientY - rect.top))*scaleY,
      };
    }
    return lastPointer!;
  };
  const handleClick = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (disabled) return;
    const pos = getPointerPosition(e);
    let clickEnd = false;
    Array.from(rects.keys()).forEach((key) => {
      if (rects.get(key)!.visible && rects.get(key)!.selected) {
        for (const { corner, x, y } of getHandlePositions(rects.get(key)!)) {
          if (
            Math.abs(pos.x - x) < 6 * lineWidth &&
            Math.abs(pos.y - y) < 6 * lineWidth
          ) {
            setResizingHandle({ rectType: key, corner });
            clickEnd = true;
            return;
          }
        }
      }
    });
    if (clickEnd) return;
    const isRectSelected = tryRectSelect(pos);
    if (!isRectSelected) {
      setStart(pos);
      setIsDrawing(true);
      setTempRect({
        x: pos.x,
        y: pos.y,
        w: 0,
        h: 0,
        selected: true,
        visible: true,
      });
    }
  };

  const handleMove = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (disabled) return;
    const pos = getPointerPosition(e);
    if (resizingHandle) {
      Array.from(rects.keys()).forEach((key) => {
        if (
          rects.get(key)!.visible &&
          rects.get(key)!.selected &&
          key === resizingHandle.rectType
        ) {
          let { x, y, w, h } = rects.get(key)!;
          switch (resizingHandle.corner) {
            case Corner.TopLeft:
              w = Math.max(2, w + (x - pos.x));
              h = Math.max(2, h + (y - pos.y));
              x = pos.x;
              y = pos.y;
              break;
            case Corner.TopRight:
              w = Math.max(2, pos.x - x);
              h = Math.max(2, h + (y - pos.y));
              y = pos.y;
              break;
            case Corner.BottomLeft:
              w = Math.max(2, w + (x - pos.x));
              x = pos.x;
              h = Math.max(2, pos.y - y);
              break;
            case Corner.BottomRight:
              w = Math.max(2, pos.x - x);
              h = Math.max(2, pos.y - y);
              break;
          }
          let rect = rects.get(key)!;
          rect.w = w;
          rect.h = h;
          rect.y = y;
          rect.x = x;
          let rectsCopy = new Map(rects);
          rectsCopy.set(key, rect);
          setRects(rectsCopy);
        }
      });
    } else if (isDrawing && start) {
      const temp = {
        x: Math.min(start.x, pos.x),
        y: Math.min(start.y, pos.y),
        color: "gray",
        w: Math.abs(pos.x - start.x),
        h: Math.abs(pos.y - start.y),
        selected: true,
        visible: true,
      };
      setTempRect(temp);
    } else if (rectMoving && dragOffset) {
      Array.from(rects.keys()).forEach((key) => {
        if (
          rects.get(key)!.visible &&
          rects.get(key)!.selected &&
          key === rectMoving
        ) {
          let rect = rects.get(key)!;
          rect.x = Math.min(
            canvasRef.current!!.width - rect.w,
            Math.max(0, pos.x - dragOffset.offsetX)
          );
          rect.y = Math.min(
            canvasRef.current!!.height - rect.h,
            Math.max(0, pos.y - dragOffset.offsetY)
          );
          let rectsCopy = new Map(rects);
          rectsCopy.set(key, rect);
          setRects(rectsCopy);
        }
      });
    }
  };

  const handleClickEnd = async (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (disabled) return;
    const pos = getPointerPosition(e);
    setDragOffset(null);
    setRectMoving(null);
    if (start && pos.x !== start.x && pos.y !== start.y) {
      const canvas = canvasRef.current;
      if (canvas) canvas.removeEventListener("touchmove", preventTouchScroll);
      if (isDrawing && tempRect && rectType!=null) {
        let rectsCopy = new Map(rects);
        rectsCopy.set(rectType, tempRect);
        setRects(rectsCopy);
      }
    } else {
      let rectsCopy = new Map(rects);
      Array.from(rects.keys()).forEach((key) => {
        if (
          rects.get(key)!.visible &&
          rects.get(key)!.selected &&
          !(
            pos.x >= rects.get(key)!.x &&
            pos.x <= rects.get(key)!.x + rects.get(key)!.w &&
            pos.y >= rects.get(key)!.y &&
            pos.y <= rects.get(key)!.y + rects.get(key)!.h
          )
        ) {
          let rect = rects.get(key)!;
          rect.selected = false;
          rectsCopy.set(key, rect);
        }
      });
      setRects(rectsCopy);
    }

    setIsDrawing(false);
    setStart(null);
    setTempRect(null);
    setResizingHandle(null);
  };

  const tryRectSelect = (pos: { x: number; y: number }) => {
    let selected = false;
    let selectedRect: RectType = "main";
    let rectsCopy = new Map(rects);
    Array.from(rects.keys()).forEach((key) => {
      if (
        rects.get(key)!.visible &&
        pos.x >= rects.get(key)!.x &&
        pos.x <= rects.get(key)!.x + rects.get(key)!.w &&
        pos.y >= rects.get(key)!.y &&
        pos.y <= rects.get(key)!.y + rects.get(key)!.h
      ) {
        selectedRect = key;
        if (!rects.get(key)!.selected) {
          let rect = rects.get(key)!;
          rect.selected = true;
          rectsCopy.set(key, rect);
        }
        setRectMoving(key);
        setDragOffset({
          offsetX: pos.x - rects.get(key)!.x,
          offsetY: pos.y - rects.get(key)!.y,
        });
        selected = true;
        return;
      }
    });
    Array.from(rects.keys()).forEach((key) => {
      let rect = rects.get(key)!;
      if (key !== selectedRect) {
        rect.selected = false;
        rectsCopy.set(key, rect);
      }
    });
    setRects(rectsCopy);
    return selected;
  };

  return (
    <canvas
      ref={canvasRef}
      style={{ maxWidth: "100%", maxHeight: "45vh", width: "auto", height: "auto", display: "block", margin: "0 auto" }}
      onMouseDown={handleClick}
      onMouseMove={handleMove}
      onMouseUp={handleClickEnd}
      onTouchStart={handleClick}
      onTouchMove={handleMove}
      onTouchEnd={handleClickEnd}
      className="border border-gray-400"
    />
  );
};
